import type { ActivityLogDto, ActivityFeedPage } from '../types';
import type { PillId } from './kindMap';

/**
 * The panel feed's paging/filter state machine.
 *
 * All of it lives here rather than in the component because the runner has no DOM (vite.config.ts
 * sets no test environment, and jsdom is not installed), so a reducer is the only part of the panel
 * that can be tested at all. The component that consumes this stays a projection of `FeedState` and
 * a dispatcher — no derived state, no second copy of these rules.
 *
 * Every rule below is a hazard that has a specific wrong-looking screen attached to it; each is
 * named where it is enforced.
 */

export interface FeedCursor {
  occurredAtUtc: string;
  id: number;
}

export interface FeedState {
  items: ActivityLogDto[];
  /** Both halves travel together — one alone is a 400, never a reset to page 1. */
  cursor: FeedCursor | null;
  hasMore: boolean;
  /** The `M` in `Showing N of M`. Whole-record, ignoring any filter. First page only, then held. */
  totalCount: number | null;
  /** Whole-record counts per kind, feeding the filter pills. Same first-page-only rule. */
  kindCounts: Record<string, number> | null;
  pill: PillId;
  /**
   * TWO independent flags, not one slot, because the two requests genuinely overlap: the confirm-poll
   * refetches page 1 on its own schedule while a "show older" may still be in flight.
   *
   * ⚠️ This was a single `'idle' | 'first' | 'more'` until review. With one slot, a page-1 response
   * landing mid-append set it to `'idle'`, which re-enabled the "show older" button and let the user fire
   * a second identical cursor request — de-duped by `appendUnique` afterwards, so the wasted round trip
   * was invisible rather than absent.
   *
   * `first` is where a skeleton belongs (the whole list is being replaced); `more` is a footer-local busy
   * state on a list that is still fully readable.
   */
  loading: { first: boolean; more: boolean };
  /** Additive — see the `failed` case. Never clears `items`. */
  error: string | null;
  /**
   * Bumped only when the cursor chain is abandoned (a filter change). A response that names an older
   * generation is dropped: without this, switching from All to Edits while a "show more" is in
   * flight lands unfiltered rows under the Edits pill, and the user sees the filter apparently fail.
   *
   * ⚠️ Deliberately NOT bumped by a page-1 refetch. The confirm-poll refetches page 1 while an
   * append may be in flight, and dropping that append would silently eat rows the user asked for.
   * That overlap is handled by de-duping instead (see {@link appendUnique}).
   */
  generation: number;
}

export const INITIAL_FEED_STATE: FeedState = {
  items: [],
  cursor: null,
  hasMore: false,
  totalCount: null,
  kindCounts: null,
  pill: 'all',
  loading: { first: false, more: false },
  error: null,
  generation: 0,
};

export type FeedAction =
  /** A page-1 request started: first mount, a retry, or a confirm-poll refetch. */
  | { type: 'first-page-requested' }
  /** A cursor request started. */
  | { type: 'more-requested' }
  | { type: 'page-received'; page: ActivityFeedPage; append: boolean; generation: number }
  | { type: 'request-failed'; message: string; generation: number; append: boolean }
  | { type: 'filter-selected'; pill: PillId };
// ⚠️ There is deliberately NO `reset` action. A record change REMOUNTS the panel (see `ActivityDock`), so
// the fresh state is `INITIAL_FEED_STATE` by construction. The action that used to be here was dispatched
// from an effect and advanced `generation` AFTER the fetch effect had already captured the old value —
// which meant the very first page of every mount was fetched and then discarded, and the panel showed
// nothing until the user switched sections and back.

/**
 * Concatenate, dropping any id already present.
 *
 * The server's keyset paging makes an overlap unlikely but not impossible: the confirm-poll refetches
 * page 1 on its own schedule, so a page-1 response can land *after* an append that already carried
 * some of the same rows, and React keys off `id`. Duplicate keys are the failure mode where a row
 * renders twice and one of the two stops updating. De-duping here costs one Set per append and makes
 * the overlap unobservable, which is why the generation guard is allowed to ignore it.
 *
 * Incoming rows are also de-duped against each other, so a server-side repeat cannot slip through.
 */
export function appendUnique(existing: ActivityLogDto[], incoming: ActivityLogDto[]): ActivityLogDto[] {
  const seen = new Set(existing.map((item) => item.id));
  const merged = [...existing];

  for (const item of incoming) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    merged.push(item);
  }

  return merged;
}

export function feedReducer(state: FeedState, action: FeedAction): FeedState {
  switch (action.type) {
    case 'first-page-requested':
      // Items stay on screen while page 1 reloads. A confirm-poll refetch that blanked the feed for
      // ~200ms after every mutation would flicker the panel on each save.
      return { ...state, loading: { ...state.loading, first: true }, error: null };

    case 'more-requested':
      return { ...state, loading: { ...state.loading, more: true }, error: null };

    case 'page-received': {
      if (action.generation !== state.generation) return state;

      const { page } = action;

      // ⚠️⚠️ A PAGE-1 RESPONSE MERGES INTO THE HEAD; IT DOES NOT REPLACE.
      //
      // This branch ran `items: page.items` until it was reviewed, and that was a user-visible bug on every
      // single save. The confirm-poll refetches page 1 after EVERY mutation, so a reader who had pressed
      // "show older" four times — 100 rows — was silently truncated back to 25 and the cursor rewound to
      // page 2. The generation guard cannot help: nothing is out of date, the replace itself is the loss.
      //
      // Merging is safe because ActivityLogs is APPEND-ONLY. Page 1 is the newest rows, the rows already
      // held are older, so `page-1 ++ existing` is already in the feed's own order, and `appendUnique`
      // drops the overlap. A row can never be deleted underneath us, so nothing stale can survive.
      const merged = action.append
        ? appendUnique(state.items, page.items)
        : appendUnique(page.items, state.items);

      // ...and the cursor follows the same logic. A page-1 response's cursor points at row 26; if we are
      // still holding rows past that, adopting it would make the next "show older" re-fetch pages the user
      // already has. Keep the deeper cursor we already had — but ONLY when the page actually OVERLAPS the
      // rows in hand, which is what proves the held cursor still covers everything between.
      //
      // ⚠️ The discriminator is "did the merge dedup anything", NOT "is the merge bigger than the page".
      // This line shipped as `merged.length > page.items.length`, which is true in BOTH the overlap case
      // and the disjoint case — and in the disjoint case it was a data-loss bug: a page-1 refetch that
      // returns a FULL page of rows all newer than anything held (25+ writes inside one poll interval)
      // leaves a GAP between the new page's bottom and the old head. Keeping the deep cursor made that
      // gap permanently unreachable — page-1 refetches only ever see the top 25, "show older" resumed
      // BELOW the old rows, and the timeline drew unbroken across the hole. Adopting the page's cursor
      // instead lets "show older" walk down through the gap; when it reaches rows already held,
      // `appendUnique` absorbs the overlap and the deeper coverage is re-earned page by page.
      const keepDeeperCursor =
        !action.append && merged.length < page.items.length + state.items.length;

      return {
        ...state,
        items: merged,
        cursor: keepDeeperCursor
          ? state.cursor
          : page.nextCursorOccurredAtUtc !== null && page.nextCursorId !== null
            ? { occurredAtUtc: page.nextCursorOccurredAtUtc, id: page.nextCursorId }
            : null,
        hasMore: keepDeeperCursor ? state.hasMore : page.hasMore,
        // ⚠️ `??`, never `=`. Both aggregates are NULL on every cursored request by design — they are
        // whole-record COUNT(*)s taken once. Assigning them straight through would zero the footer's
        // M and empty the pill row the instant anyone pressed "show older".
        totalCount: page.totalCount ?? state.totalCount,
        kindCounts: page.kindCounts ?? state.kindCounts,
        // Clear only the flag belonging to THIS response, so one request's completion cannot unblock
        // the other's control.
        loading: action.append
          ? { ...state.loading, more: false }
          : { ...state.loading, first: false },
        error: null,
      };
    }

    case 'request-failed':
      if (action.generation !== state.generation) return state;
      // Additive: the feed already on screen is still true and still readable. A failed "show older"
      // that emptied the list would turn a recoverable network blip into a lost page.
      return {
        ...state,
        // Same rule as the success path: a failed append must not clear a page-1 request's busy state.
        loading: action.append
          ? { ...state.loading, more: false }
          : { ...state.loading, first: false },
        error: action.message,
      };

    case 'filter-selected': {
      if (action.pill === state.pill) return state;

      return {
        ...state,
        pill: action.pill,
        // A new filter is a new cursor chain: page 1, no cursor, nothing carried over.
        items: [],
        cursor: null,
        hasMore: false,
        // …but the aggregates SURVIVE. They describe the record, not the filter, and the footer's
        // rule is that M is fixed while N narrows (design §1.5). Clearing them would also collapse
        // the pill row itself — the control you just used would disappear as you used it.
        totalCount: state.totalCount,
        kindCounts: state.kindCounts,
        loading: { first: true, more: false },
        error: null,
        generation: state.generation + 1,
      };
    }

    default:
      return state;
  }
}

/**
 * `Showing N of M` (design §1.5). N is what is loaded — including under a filter, where it is
 * legitimately far below M — and M is null until the first page answers, in which case the caller
 * renders N alone rather than `of 0`.
 */
export function selectFooterCounts(state: FeedState): { shown: number; total: number | null } {
  return { shown: state.items.length, total: state.totalCount };
}

/** A "show older" button is live only with a cursor to spend and nothing already in flight. */
export function selectCanLoadMore(state: FeedState): boolean {
  return state.hasMore && state.cursor !== null && !state.loading.more;
}

/** The skeleton case: page 1 in flight with nothing yet to show. A reload keeps the old rows. */
export function selectShowSkeleton(state: FeedState): boolean {
  return state.loading.first && state.items.length === 0;
}
